/**
 * resolveBundleLabel
 *
 * Single source of truth for the display name of an ArTable bundle, shared by
 * ArBundleHeaderRow (screen and PDF), useArTableBundleRows (bundle map) and
 * createBundleHeaderCellReader (XLSX), so no surface can print a different name
 * than the grid (SYD-2276). Pure function — no store access, no React.
 *
 * Invariant: a bundle always has a name. A reference or manual value missing its
 * own label falls back to the row id; a header carrying no `bundleValue` at all
 * — every legacy group — falls back to the bundle type, 'Kit' or 'Group'.
 *
 * `bundleValue` is typed as a Valtio snapshot so a deeply-readonly header row
 * passes unchanged; a plain persisted `ArBundleValue` is assignable to it.
 */

import type { ArBundleType, ArBundleValue, ArRowId } from '#types';
import type { Snapshot } from 'valtio';

function resolveBundleLabel(params: {
  bundleValue: Snapshot<ArBundleValue> | undefined;
  bundleType: ArBundleType | undefined;
  rowId: ArRowId;
}): string {
  const { bundleValue, bundleType, rowId } = params;

  if (typeof bundleValue === 'string') return bundleValue;
  if (bundleValue && 'cacheLabel' in bundleValue) return bundleValue.cacheLabel ?? rowId;
  if (bundleValue && 'value' in bundleValue) return bundleValue.value ?? rowId;
  return bundleType === 'kit' ? 'Kit' : 'Group';
}

export { resolveBundleLabel };
